import React from "react";
import PropTypes from "prop-types";
import Link from "gatsby-link";
import styled, { css } from "react-emotion";
import { Redirect } from "react-router-dom";
import { sort, ascend, identity, findIndex } from "ramda";
import { darken } from "polished";

import { getSelectedIndicator } from "../utils";
import { colors } from "../consts";
import mapData from "../data/map-data";
import MTMap from "../components/MTMap";
import CountyList from "../components/CountyList";
import IndicatorInfo from "../components/IndicatorInfo";
import MarkdownContent from "../components/MarkdownContent";
import { MarkdownStringContent } from "../components/Content";
import montanaCounties from "../data/montana-counties.json";

const countiesSorted = sort(
  ascend(identity),
  montanaCounties.objects.cb_2015_montana_county_20m.geometries.map(
    g => g.properties.NAME
  )
);
const getDataSheetUrl = countyName => {
  if (!countyName) return null;
  const index = findIndex(c => c === countyName, countiesSorted);
  return `/datasheets/MBPC_CountyData_8.5x11_County${index + 1}.pdf`;
};

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  position: relative;
`;
const DataContainer = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
`;
const MapContainer = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
`;
const FooterLinks = styled.div`
  position: absolute;
  bottom: 10px;
  width: 50%;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  justify-content: center;
  & > div {
    padding-right: 10px;
    margin-right: 10px;
    border-right: 1px solid ${colors.primary};
    &:last-child {
      padding-right: 0;
      margin-right: 0;
      border-right: none;
    }
  }
`;
const FooterLink = styled(Link)`
  color: ${colors.primary};
  text-decoration: none;
  transition-duration: 0.2s;
  &:hover {
    color: ${darken(0.1, colors.primary)};
  }
`;

export default class MapPage extends React.Component {
  state = { hoveredCounty: null };

  render() {
    const {
      data: {
        map: {
          frontmatter: { indicators }
        },
        acknowledgments: { html: acknowledgments },
        methodology: { html: methodology }
      }
    } = this.props;
    const { hoveredCounty } = this.state;
    const selectedIndicator = getSelectedIndicator(
      this.props.location,
      indicators
    );

    if (!this.props.location.search) {
      return <Redirect to={`/map?key=${indicators[0].dataKey}`} />;
    }

    const indicatorData = mapData[selectedIndicator.dataKey];
    const dataSheetUrl = getDataSheetUrl(hoveredCounty);

    return (
      <Wrapper>
        <DataContainer>
          <CountyList
            indicatorData={indicatorData}
            hoveredCounty={hoveredCounty}
            setHoveredCounty={this.setHoveredCounty}
            dataSheetUrl={dataSheetUrl}
          />
          <IndicatorInfo
            indicator={selectedIndicator}
            indicatorData={indicatorData}
          />
        </DataContainer>
        <MapContainer>
          <MTMap
            indicatorData={indicatorData}
            hoveredCounty={hoveredCounty}
            setHoveredCounty={this.setHoveredCounty}
            dataSheetUrl={dataSheetUrl}
          />
        </MapContainer>

        <FooterLinks>
          <div>
            <FooterLink to="/acknowledgments">Acknowledgments</FooterLink>
          </div>
          <div>
            <FooterLink to="/methodology">Methodology</FooterLink>
          </div>
        </FooterLinks>
      </Wrapper>
    );
  }

  setHoveredCounty = hoveredCounty => {
    this.setState({ hoveredCounty });
  };
}

MapPage.propTypes = {
  data: PropTypes.shape({
    markdownRemark: PropTypes.object
  })
};

export const pageQuery = graphql`
  query MapPageByPath {
    map: markdownRemark(frontmatter: { path: { eq: "/map" } }) {
      frontmatter {
        indicators {
          dataKey
          title
          description
          notes
          source
        }
      }
    }
    acknowledgments: markdownRemark(
      frontmatter: { path: { eq: "/acknowledgments" } }
    ) {
      html
    }
    methodology: markdownRemark(frontmatter: { path: { eq: "/methodology" } }) {
      html
    }
  }
`;



// WEBPACK FOOTER //
// ./src/pages/map.js